import { isCapacitorRuntime } from "../config/vars";

export const THEME_MODES = ["light", "dark", "system"];
export const THEME_STORAGE_KEY = "WR_THEME_MODE";
export const THEME_CHANGE_EVENT = "wr-theme-changed";

function getCapPlugins() {
    try {
        return window?.Capacitor?.Plugins;
    } catch (_) {
        return null;
    }
}

function normalizeMode(mode) {
    return THEME_MODES.includes(mode) ? mode : "system";
}

function getSystemResolved() {
    try {
        return window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";
    } catch (_) {
        return "light";
    }
}

export function resolveTheme(mode) {
    const normalized = normalizeMode(mode);
    return normalized === "system" ? getSystemResolved() : normalized;
}

export function getThemeModeSync() {
    try {
        return normalizeMode(localStorage.getItem(THEME_STORAGE_KEY) || "system");
    } catch (_) {
        return "system";
    }
}

export function applyThemeMode(mode) {
    const normalized = normalizeMode(mode);
    const resolved = resolveTheme(normalized);
    const root = document.documentElement;
    root.setAttribute("data-theme", resolved);
    root.setAttribute("data-theme-mode", normalized);
    root.style.colorScheme = resolved;
    return resolved;
}

async function prefGet(key) {
    const plugins = getCapPlugins();
    const Preferences = plugins?.Preferences;
    if (isCapacitorRuntime() && Preferences?.get) {
        const res = await Preferences.get({ key });
        return res?.value ?? null;
    }
    try {
        return localStorage.getItem(key);
    } catch (_) {
        return null;
    }
}

async function prefSet(key, value) {
    const plugins = getCapPlugins();
    const Preferences = plugins?.Preferences;
    if (isCapacitorRuntime() && Preferences?.set) {
        await Preferences.set({ key, value });
    }
    try {
        localStorage.setItem(key, value);
    } catch (_) {
        // ignore
    }
}

export async function getThemeMode() {
    const stored = await prefGet(THEME_STORAGE_KEY);
    return normalizeMode(stored || getThemeModeSync());
}

export async function setThemeMode(mode) {
    const normalized = normalizeMode(mode);
    await prefSet(THEME_STORAGE_KEY, normalized);
    const resolved = applyThemeMode(normalized);
    try {
        window.dispatchEvent(
            new CustomEvent(THEME_CHANGE_EVENT, {
                detail: { mode: normalized, resolved },
            })
        );
    } catch (_) {
        // ignore
    }
    return resolved;
}

export function initTheme() {
    applyThemeMode(getThemeModeSync());

    let media;
    try {
        media = window.matchMedia("(prefers-color-scheme: dark)");
    } catch (_) {
        media = null;
    }

    const onSystemChange = () => {
        if (getThemeModeSync() === "system") {
            applyThemeMode("system");
        }
    };

    if (media) {
        if (media.addEventListener) {
            media.addEventListener("change", onSystemChange);
        } else if (media.addListener) {
            media.addListener(onSystemChange);
        }
    }

    getThemeMode().then((mode) => {
        applyThemeMode(mode);
    }).catch(() => {
        // ignore
    });

    return () => {
        if (!media) return;
        if (media.removeEventListener) {
            media.removeEventListener("change", onSystemChange);
        } else if (media.removeListener) {
            media.removeListener(onSystemChange);
        }
    };
}
